/**
 * Return Order Items Component
 *
 * Displays the table of returned items with their details,
 * pricing, and lot information.
 */

import {
  Box,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography
} from "@mui/material"
import { getDisplayPrice } from "src/shared/utils/helpers-functions"
import Stack from "src/shared/components/stack"
type ReturnOrderItemsProps = {
  detail: any
}

/**
 * Items table component for return order details
 */
export default function ReturnOrderItems({ detail }: ReturnOrderItemsProps) {
  return (
    <Card variant="outlined">
      <CardContent>
        {/* Header with title and summary chips */}
        <Stack variant="rowCenterSpaceBetween" mb={2}>
          <Typography variant="h6" color="primary">
            Returned Items
          </Typography>
          <Stack variant="rowCenter" gap={1}>
            <Chip size="small" label={`${detail.items.length} items`} color="success" variant="outlined" />
            <Chip
              size="small"
              color="primary"
              label={`Total: ${getDisplayPrice(detail.totalAmount)}`}
              sx={{ fontWeight: 500 }}
            />
          </Stack>
        </Stack>

        {/* Items Table */}
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Unit Price
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Qty
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Discount
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Subtotal
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Total
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {detail.items.map((item: any) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{
                    "&:hover": {
                      backgroundColor: "action.hover",
                      transform: "scale(1.001)",
                      transition: "all 0.2s"
                    }
                  }}
                >
                  {/* Product Information */}
                  <TableCell>
                    <Stack gap={1}>
                      <Box>
                        <Typography variant="body2">
                          {item.name}
                        </Typography>
                        {item.description && (
                          <Typography variant="caption" color="text.secondary">
                            {item.description}
                          </Typography>
                        )}
                        {item.lots?.length ? (
                          <Typography
                            variant="caption"
                            color="primary"
                            display="block"
                            sx={{
                              mt: 0.5,
                              backgroundColor: "primary.50",
                              py: 0.25,
                              borderRadius: 0.5,
                              display: "inline-block"
                            }}
                          >
                            Lots: {item.lots.map((l: any) => `${l.productLot.actionId}`).join(", ")}
                          </Typography>
                        ) : null}
                      </Box>
                    </Stack>
                  </TableCell>

                  {/* Unit Price */}
                  <TableCell align="right">
                    <Typography variant="body2">
                      {getDisplayPrice(item.unitPrice)}
                    </Typography>
                  </TableCell>

                  {/* Quantity */}
                  <TableCell align="right">
                    <Chip size="small" label={item.quantity} variant="outlined" color="success" />
                  </TableCell>

                  {/* Discount */}
                  <TableCell align="right">
                    {item.discount ? (
                      <Typography variant="body2" color="error.main">
                        -{getDisplayPrice(item.discount)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>

                  {/* Subtotal */}
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {getDisplayPrice(item.subTotal)}
                    </Typography>
                  </TableCell>

                  {/* Total */}
                  <TableCell align="right">
                    <Typography variant="body2" color="primary">
                      {getDisplayPrice(item.totalPrice)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            {/* Table Footer with Total */}
            <TableFooter>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell colSpan={4} />
                <TableCell align="right">
                  <Typography variant="subtitle1" fontWeight={600}>
                    Total
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle1" fontWeight={600} color="primary">
                    {getDisplayPrice(detail.totalAmount)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )
}
